import React, { useState } from "react";

import { API_BASE_URL } from "../config/apiBase";
import DevStudentResetPanel from "./DevStudentResetPanel";
import { studentLessonPathFor } from "../utils/lessonRoute";
import { logoutToLogin } from "../utils/sessionLogout";
import { fetchTargetIdentity, normalizeStudentId } from "../utils/targetIdentity";

const API = API_BASE_URL;

export default function StudentAdminSidebar({
  studentId,
  currentRoute,
  navigate,
  closeMobileDrawer,
  showStudentProgressLink = true,
  showTutorDashboardLink = true,
  showDevResetTools = true,
  llmUsage,
}) {
  const [resettingProgress, setResettingProgress] = useState(false);
  const resetIdentityRequestRef = React.useRef(0);
  const currentPath = String(currentRoute || "");
  const dashboardBase = `/student/dashboard/${studentId}`;
  const onDashboardHome = currentPath === dashboardBase;
  const onLesson = currentPath.includes("/lessons");
  const onDashboardV2 = currentPath.includes("/dashboard_v2");
  const onTutorStudentProgress = currentPath.startsWith("/tutor/student-progress");
  const onNotifications = currentPath.includes("/notifications");
  const onTutorNotes = currentPath.includes("/tutor-notes");
  const onBadges = currentPath.includes("/badges");
  const onAccount = currentPath.includes("/account");

  const closeDrawer = () => {
    if (typeof closeMobileDrawer === "function") {
      closeMobileDrawer();
    }
  };

  const goTo = (path, options) => {
    navigate(path, options);
    closeDrawer();
  };

  const goToLessons = () => goTo(`${dashboardBase}/lessons`);
  const goToDashboardV2 = () => goTo(`${dashboardBase}/dashboard_v2`);
  const goToNotifications = () => goTo(`${dashboardBase}/notifications`);
  const goToTutorNotes = () => goTo(`${dashboardBase}/tutor-notes`);
  const goToBadges = () => goTo(`${dashboardBase}/badges`);
  const goToAccount = () => goTo(`${dashboardBase}/account`);

  const goToLessonPosition = (week, lesson, options = {}) => {
    const w = Number(week) || 1;
    const l = Number(lesson) || 1;
    const query = options?.devJump ? `?dev_jump=1&jump_ts=${Date.now()}` : "";
    navigate(studentLessonPathFor(studentId, w, l) + query);
    closeDrawer();
  };

  const handleResetProgress = async () => {
    if (!studentId || resettingProgress) return;
    const normalizedStudentId = normalizeStudentId(studentId);
    const requestToken = ++resetIdentityRequestRef.current;
    let targetIdentity;
    try {
      targetIdentity = await fetchTargetIdentity(normalizedStudentId);
    } catch {
      window.alert("Could not find the selected target student.");
      return;
    }
    if (requestToken !== resetIdentityRequestRef.current || targetIdentity.studentId !== normalizedStudentId) return;
    const confirmed = window.confirm(
      `Reset ALL COURSE PROGRESS for ${targetIdentity.name || "the student"} (Target ID ${normalizedStudentId})? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setResettingProgress(true);
      const res = await fetch(`${API}/api/dev/student-reset/full`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: normalizedStudentId }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.ok === false) {
        throw new Error((data && data.detail) || "Failed to reset progress.");
      }
      try {
        const sid = String(studentId || "").toUpperCase();
        if (sid) {
          window.dispatchEvent(new CustomEvent("studentXpUpdated", { detail: { studentId: sid } }));
          window.dispatchEvent(
            new CustomEvent("writeflow:lesson-reset", { detail: { studentId: sid } })
          );
        }
      } catch {
        // non-blocking
      }
      try {
        window.dispatchEvent(
          new CustomEvent("studentProgressRefetch", {
            detail: {
              studentId,
              currentWeek: Number(data?.state?.current_week) || 1,
              currentLesson: Number(data?.state?.current_lesson) || 1,
            },
          })
        );
      } catch {
        // non-blocking
      }
      navigate(studentLessonPathFor(studentId, 1, 1), { replace: true });
      closeDrawer();
    } catch {
      window.alert("Could not reset progress. Please try again.");
    } finally {
      setResettingProgress(false);
    }
  };

  const handleLogout = () => logoutToLogin(navigate);

  return (
    <>
      <div className="sp-side-top">
        <div className="sp-side-brand">
          <img
            src={process.env.PUBLIC_URL + "/hirilogo.png"}
            alt="HIRI logo"
            className="sp-side-logo"
          />
          <div className="sp-side-text">
            <div className="sp-side-title">Writing Studio</div>
            <div className="sp-side-sub">Student portal</div>
          </div>
        </div>

        <nav className="sp-side-nav" data-tour="dashboard-sidepanel">
          <button
            className={`sp-navitem ${onDashboardHome ? "active" : ""}`}
            type="button"
            onClick={() => goTo(dashboardBase)}
          >
            Dashboard
          </button>
          <button className={`sp-navitem ${onLesson ? "active" : ""}`} type="button" onClick={goToLessons}>
            Lessons
          </button>
          {showStudentProgressLink ? (
            <button
              className={`sp-navitem ${onDashboardV2 ? "active" : ""}`}
              type="button"
              onClick={goToDashboardV2}
            >
              Progress
            </button>
          ) : null}
          {showStudentProgressLink ? (
            <button
              className={`sp-navitem ${onTutorStudentProgress ? "active" : ""}`}
              type="button"
              onClick={() => goTo("/tutor/student-progress")}
            >
              Student Progress
            </button>
          ) : null}
          <button
            className={`sp-navitem ${onNotifications ? "active" : ""}`}
            type="button"
            onClick={goToNotifications}
          >
            Notifications
          </button>
          <button
            className={`sp-navitem ${onTutorNotes ? "active" : ""}`}
            type="button"
            onClick={goToTutorNotes}
          >
            Tutor Notes
          </button>
          <button className={`sp-navitem ${onBadges ? "active" : ""}`} type="button" onClick={goToBadges}>
            Badges
          </button>
          <button className={`sp-navitem ${onAccount ? "active" : ""}`} type="button" onClick={goToAccount}>
            Account
          </button>
          {showTutorDashboardLink ? (
            <button
              className="sp-navitem"
              type="button"
              onClick={() => goTo("/tutor/dashboard_v2", { state: { returnTo: currentRoute } })}
            >
              Tutor Dashboard
            </button>
          ) : null}
          {showTutorDashboardLink ? (
            <button className="sp-navitem" type="button" onClick={() => goTo("/admin/users")}>
              Users
            </button>
          ) : null}
        </nav>
      </div>

      <div className="sp-side-bottom">
        <div className="sp-id-chip">ID: {studentId}</div>
        {showDevResetTools ? (
          <>
            <button
              className="sp-link sp-link-danger"
              type="button"
              onClick={handleResetProgress}
              disabled={resettingProgress}
            >
              {resettingProgress ? "Resetting..." : "Reset Progress"}
            </button>

              <DevStudentResetPanel
                defaultStudentId={studentId}
                onNavigateLesson={goToLessonPosition}
                refreshSignal={llmUsage?.session_total_requests || 0}
              />
          </>
        ) : null}
        <button className="sp-link" type="button" onClick={handleLogout}>
          Log out
        </button>
      </div>
    </>
  );
}
